import React from 'react';
import { useDashboard } from '../../hooks/useDashboard';
import { usePageTitle } from '../../hooks/usePageTitle';

const Dashboard: React.FC = () => {
  usePageTitle('Dashboard');
  const { data: stats, isLoading, error } = useDashboard();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando estatísticas...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">Erro ao carregar dados do dashboard</div>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDateTime = (dateString: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      // Status de pedidos
      'Aguardando Pagamento': 'text-yellow-600 bg-yellow-100',
      'Pago': 'text-green-600 bg-green-100',
      'Cancelado': 'text-red-600 bg-red-100',
      'Estornado': 'text-gray-600 bg-gray-100',
      // Status de encomendas
      'Aguardando': 'text-blue-600 bg-blue-100',
      'EmProducao': 'text-orange-600 bg-orange-100',
      'Pronto': 'text-purple-600 bg-purple-100',
      'Entregue': 'text-green-600 bg-green-100',
    };
    return statusColors[status] || 'text-gray-600 bg-gray-100';
  };

  const getStatusLabel = (status: string) => {
    const statusLabels: { [key: string]: string } = {
      // Status de pedidos
      'Aguardando Pagamento': 'Aguardando Pgto.',
      'Pago': 'Pago',
      'Cancelado': 'Cancelado',
      'Estornado': 'Estornado',
      // Status de encomendas
      'Aguardando': 'Aguardando',
      'EmProducao': 'Em Produção',
      'Pronto': 'Pronto',
      'Entregue': 'Entregue',
    };
    return statusLabels[status] || status;
  };

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Vendas Hoje</p>
              <p className="text-2xl font-bold text-gray-800">
                {formatCurrency(stats?.totalVendasHoje || 0)}
              </p>
            </div>
            <div className="text-4xl">💰</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Encomendas Hoje</p>
              <p className="text-2xl font-bold text-gray-800">{stats?.encomendasHoje || 0}</p>
            </div>
            <div className="text-4xl">📋</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Em Produção</p>
              <p className="text-2xl font-bold text-gray-800">{stats?.itensProducao || 0}</p>
            </div>
            <div className="text-4xl">👨‍🍳</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Estoque Baixo</p>
              <p className="text-2xl font-bold text-red-600">
                {stats?.ingredientesBaixoEstoque || 0}
              </p>
            </div>
            <div className="text-4xl">⚠️</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Últimas Vendas</h2>
          {stats?.ultimasVendas && stats.ultimasVendas.length > 0 ? (
            <div className="space-y-3">
              {stats.ultimasVendas.map((venda) => (
                <div
                  key={venda.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">Pedido #{venda.id}</p>
                    <p className="text-xs text-gray-500">{formatDateTime(venda.dataHora)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                        venda.status
                      )}`}
                    >
                      {getStatusLabel(venda.status)}
                    </span>
                    <p className="text-sm font-bold text-gray-800">
                      {formatCurrency(venda.valorTotal)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Nenhuma venda registrada hoje</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Encomendas do Dia</h2>
          {stats?.encomendasDoDia && stats.encomendasDoDia.length > 0 ? (
            <div className="space-y-3">
              {stats.encomendasDoDia.map((encomenda) => (
                <div
                  key={encomenda.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">{encomenda.clienteNome}</p>
                    <p className="text-xs text-gray-500">
                      Entrega: {formatDateTime(encomenda.dataHoraEntrega)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                        encomenda.status
                      )}`}
                    >
                      {getStatusLabel(encomenda.status)}
                    </span>
                    <p className="text-sm font-bold text-gray-800">
                      {formatCurrency(encomenda.valorTotal)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Nenhuma encomenda para hoje</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
